import axios, { HttpStatusCode, } from 'axios';
import { createApiError, ApiError } from './errors';
const RETRIED_FLAG = '_retriedAfterRefresh';
/**
 * Prefer `message` when it is a string. If `message` is absent (`undefined`/`null`),
 * use `error` when it is a string (e.g. `{ error: "…" }` with no `message`).
 * If `message` is present but not a string, fall back to the axios message — do not
 * substitute `error`, so malformed bodies do not mask the network error.
 */
function extractApiErrorMessage(data, fallback) {
    if (!data || typeof data !== 'object') {
        return fallback;
    }
    const d = data;
    if (typeof d.message === 'string') {
        return d.message;
    }
    const messageAbsent = d.message === undefined || d.message === null;
    if (messageAbsent && typeof d.error === 'string') {
        return d.error;
    }
    return fallback;
}
/**
 * Creates a configured axios instance for API calls
 */
export function createApiClient(config) {
    const { baseURL = 'https://api.getkirin.com', getToken, onError, onUnauthorized, timeout = 30000, } = config;
    const client = axios.create({
        baseURL,
        timeout,
        headers: {
            'Content-Type': 'application/json',
        },
    });
    // Request interceptor: Inject Bearer token
    client.interceptors.request.use(async (requestConfig) => {
        try {
            const token = await getToken();
            if (token) {
                requestConfig.headers.Authorization = `Bearer ${token}`;
            }
        }
        catch {
            // If token retrieval fails, continue without auth header
            // The server will return 401 if auth is required
        }
        return requestConfig;
    }, (error) => {
        return Promise.reject(error);
    });
    // Response interceptor: Handle errors and extract data
    client.interceptors.response.use(response => {
        // Return just the data for successful responses
        return response.data;
    }, async (error) => {
        // Axios cancellations (component unmount, react-query refetch, navigation)
        // are not API errors — passthrough so callers see CanceledError and Sentry
        // doesn't get flooded with synthetic ServerError(500). KIR-2601.
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }
        if (axios.isAxiosError(error)) {
            const status = error.response?.status ?? 500;
            // 401 retry: refresh the token and replay the request once.
            // The flag is set on the config object so it survives Axios's internal
            // config merge when client.request() creates a new request.
            if (status === HttpStatusCode.Unauthorized &&
                onUnauthorized &&
                error.config &&
                !error.config[RETRIED_FLAG]) {
                error.config[RETRIED_FLAG] =
                    true;
                let refreshed = false;
                try {
                    refreshed = await onUnauthorized();
                }
                catch {
                    // Refresh callback failed; fall through to normal 401 handling
                }
                if (refreshed) {
                    const freshToken = await getToken();
                    if (freshToken) {
                        error.config.headers.Authorization = `Bearer ${freshToken}`;
                    }
                    return client.request(error.config);
                }
            }
            const data = error.response?.data;
            const fallback = error.message || 'An unknown error occurred';
            const message = extractApiErrorMessage(data, fallback);
            // Create typed API error
            const requestUrl = typeof error.config?.url === 'string' ? error.config.url : undefined;
            const apiError = createApiError(status, data, message, requestUrl);
            // Call custom error handler if provided
            if (onError) {
                onError(apiError);
            }
            return Promise.reject(apiError);
        }
        // For non-axios errors, create a generic API error
        const apiError = new ApiError(500, null, error.message || 'An unknown error occurred');
        if (onError) {
            onError(apiError);
        }
        return Promise.reject(apiError);
    });
    return client;
}
// Re-export ApiError for convenience
export { ApiError } from './errors';
//# sourceMappingURL=client.js.map